/**
 * MobileNavigationAnalyticsFunction.ts
 * Common analytics functions for mobile bottom navigation components
 */

import { dlEventConstants, headerConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import { getCookie } from "@repo/shared-utils/common-utils";

const getValueOrNA = (value: string | null | undefined): string => value?.trim() ?? "NA";

/**
 * Handle mobile bottom navigation icon click analytics.
 * Mirrors the Solid pages `BottomNavigationMobile.addActiveClass` payload so
 * the same PLP_CLICKED event fires for all 6 footer icons
 * (Home, Service, Scan QR, Offers, Menu, Chat).
 *
 * @param ctaText       - icon label (tracking text)
 * @param position      - 1-based icon position in the bar
 * @param destinationUrl- href of the icon ("" for Menu / Quick Actions)
 */
export const handleBottomNavigationIconClickDL = (
  ctaText: string,
  position: number,
  destinationUrl: string,
): void => {
  const productCategoryTag = document.querySelector('meta[name="product-category"]');
  const productCategory = productCategoryTag?.getAttribute("content") ?? "demat";

  let customerType = "NA";
  const userDetailsCookie = getCookie("userDetails");
  if (userDetailsCookie) {
    try {
      customerType =
        (JSON.parse(userDetailsCookie) as { custType?: string })?.custType ?? "NA";
    } catch {
      customerType = "NA";
    }
  }

  // Solid: EP_SOURCE is "NA" for the Menu icon, otherwise the previous page (document.referrer)
  const epSource = ctaText === "Menu" ? "NA" : getValueOrNA(document.referrer);

  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_ICONS,
    component: headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.ICON_CLICK,
    destination_url: destinationUrl,
    cta_text: ctaText,
  };

  const cleverTapObj = {
    EP_COMPONENT_NAME: getValueOrNA(
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    ),
    EP_CLICK_TYPE: "bottom_navigation_icon_click",
    EP_CTA: getValueOrNA(ctaText),
    EP_SECTION: getValueOrNA(headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE),
    EP_CTA_TEXT: getValueOrNA(ctaText),
    EP_EVENT_TYPE: getValueOrNA(dlEventConstants.CLICK),
    EP_PAGE_URL: getValueOrNA(window.location.pathname),
    EP_POSITION: String(position),
    EP_SUB_SECTION: "NA",
    EP_CUSTOMER_TYPE: customerType,
    EP_REFERENCE_ID: getValueOrNA(
      headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    ),
    EP_SOURCE: epSource,
    EP_PRODUCT_CATEGORY: getValueOrNA(productCategory),
    EP_DESTINATION: destinationUrl,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle mobile hamburger menu close button click analytics
 * Logs click event for the close button in the mobile hamburger menu
 */
export const handleHamburgerMenuCloseClickDL = (
  cta_text: string = headerConstants.MOBILE_NAVIGATION.CTA_TEXT.BOTTOM_NAVIGATION_ICONS
) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_ICONS,
    component: headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.ICON_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle mobile hamburger menu card click analytics
 * Logs click event for the menu cards in the mobile hamburger menu
 */
export const handleHamburgerMenuCardClickDL = (cta_text: string, index: number) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_MENU,
    component: headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.CARD_CLICK,
    cta_text,
    card_index: index,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
    card_index: String(data.card_index), // Convert to string for CleverTap
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle cross sell links click analytics
 * Logs click event for the cross sell links in the mobile footer
 */
export const handleCrossSellLinkClickDL = (cta_text: string) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_MENU,
    component: headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.LINK_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle hamburger menu link click analytics
 * Logs click event for the main menu links in the hamburger menu
 * @param cta_text The text of the clicked link
 * @param menuSection Optional section identifier within the hamburger menu
 */
export const handleHamburgerMenuLinkClickDL = (
  cta_text: string,
  menuSection: string = "main_menu"
) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_MENU,
    component: headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.LINK_CLICK,
    cta_text,
    menu_section: menuSection, // Added to differentiate from cross-sell links
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
    menu_section: data.menu_section,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle social media icon click analytics
 * Logs click event for the social media icons in the mobile footer
 */
export const handleSocialMediaIconClickDL = (cta_text: string) => {
  const data = {
    element: headerConstants.MOBILE_NAVIGATION.ELEMENTS.BOTTOM_NAVIGATION_MENU,
    component: headerConstants.MOBILE_NAVIGATION.COMPONENTS.BOTTOM_NAVIGATION_MOBILE,
    event: dlEventConstants.CLICK,
    event_type: headerConstants.MOBILE_NAVIGATION.EVENT_TYPES.SOCIAL_ICON_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};
